import * as styles from "./ChannelList.css";

import type { JSX, ReactNode } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import type { PlaceLabel } from "@frontend/features/addressbook";
import { useArrayField, useField } from "@frontend/hooks/form";
import type { ArrayField, Field, TextField } from "@frontend/lib/form";
import type { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";

export type ChannelListProps<TItem extends Field, TValue> = {
  field: ArrayField<TItem, TValue>;
  /** What the row adds when the list is grown. */
  blank: TValue;
  labels: PlaceLabel;
  place: (item: TItem) => TextField;
  addLabel: string;
  emptyLabel: string;
  children: (item: TItem) => ReactNode;
};

/**
 * A repeated line of the identity: where it is used, then the value. The place
 * comes from the vCard the contact was written from, so a code the list does
 * not know is offered as it stands rather than dropped.
 */
export function ChannelList<TItem extends Field, TValue>({
  field,
  blank,
  labels,
  place,
  addLabel,
  emptyLabel,
  children,
}: ChannelListProps<TItem, TValue>): JSX.Element {
  const { t } = useLingui();

  const rows = useArrayField(field);

  return (
    <div className={styles.list}>
      {rows.isEmpty ? <p className={styles.empty}>{emptyLabel}</p> : null}

      {rows.items.map((item, index) => (
        <div key={item.path} className={styles.row}>
          <Button
            variant="ghost"
            size="sm"
            className={styles.remove}
            aria-label={t`Remove`}
            onClick={() => rows.remove(rows.itemAtoms[index]!)}
          >
            <Icon name="CircleMinus" size="sm" />
          </Button>

          <PlaceSelect field={place(item)} labels={labels} />

          {children(item)}
        </div>
      ))}

      <Button
        variant="link"
        size="sm"
        leftSection={<Icon name="Plus" size="sm" />}
        onClick={() => rows.append(blank)}
      >
        {addLabel}
      </Button>
    </div>
  );
}

export type PlaceSelectProps = {
  field: TextField;
  labels: PlaceLabel;
};

/** Where the line is used, as the address book names those places. */
export function PlaceSelect({ field, labels }: PlaceSelectProps): JSX.Element {
  const { i18n, t } = useLingui();

  const { value, setValue } = useField(field);

  const key = value ? value.toUpperCase() : null;
  const unknown = null !== key && !(key in labels);

  const items = [
    ...(unknown && value ? [{ value: key, label: value }] : []),
    ...Object.entries<MessageDescriptor>(labels).map(([code, label]) => ({
      value: code,
      label: i18n._(label) ?? code,
    })),
  ];

  return (
    <Select
      items={items}
      value={key}
      size="sm"
      onValueChange={(next) => setValue(next ?? "")}
    >
      <SelectTrigger>
        <SelectValue placeholder={t`Place`} />
      </SelectTrigger>

      <SelectContent>
        {items.map((item) => (
          <SelectItem key={item.value} value={item.value}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
